import React, { useState, useRef, useEffect, CSSProperties } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Target, Layers, Scale, Zap, Activity, Waves } from "lucide-react";
import SectionHeader from "../SectionHeader";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { smartRingsData } from "./deta-ProductComparison/smartRingsData";
import { smartBandsData } from "./deta-ProductComparison/smartBandsData";

// Types
type RingColor = {
    color: string;
    bg: string;
    border: string;
};

type RingSensor = {
    name: string;
};

type RingData = {
    id: string;
    name: string;
    slogan: string;
    suitableFor: string;
    alloy: string;
    weight: string;
    batteryLife: string;
    sensors: RingSensor[];
    resistance: string;
    resistanceNote?: string;
    colors: RingColor[];
    imagePath: string;
    backgroundPath: string;
    gradientFrom: string;
    gradientTo: string;
    hoverBgColor: string;
};

// Multi-language UI texts
const SLIDER_I18N: Record<string, {
    ringTitleRegular: string;
    ringTitleHighlighted: string;
    ringDescHighlight: string;
    ringDescText: string;
    bandTitleRegular: string;
    bandTitleHighlighted: string;
    bandDescHighlight: string;
    bandDescText: string;
    suitableForLabel: string;
    alloyLabel: string;
    weightLabel: string;
    batteryLabel: string;
    sensorsLabel: string;
    resistanceLabel: string;
    andLabel: string;
}> = {
    fa: {
        ringTitleRegular: "حلقه‌های هوشمند",
        ringTitleHighlighted: "دلبند",
        ringDescHighlight: "دو انتخاب هوشمند و پیشرفته",
        ringDescText: "برای نیازهای متفاوت شما؛ از پایش روزمره سلامت تا اندازه‌گیری‌های دقیق داده‌های سلامتی با",
        bandTitleRegular: "دستبندهای هوشمند",
        bandTitleHighlighted: "دلبند",
        bandDescHighlight: "دو انتخاب هوشمند و کاربردی",
        bandDescText: "برای نیازهای متفاوت شما؛ از پایش روزمره سلامت تا رصد دقیق داده‌های حیاتی با",
        suitableForLabel: "مناسب برای:",
        alloyLabel: "جنس بدنه:",
        weightLabel: "وزن:",
        batteryLabel: "شارژدهی:",
        sensorsLabel: "سنسورها:",
        resistanceLabel: "مقاومت:",
        andLabel: "و",
    },
    en: {
        ringTitleRegular: "Smart Rings",
        ringTitleHighlighted: "Delband",
        ringDescHighlight: "Two Smart & Advanced Choices",
        ringDescText: "Tailored to your needs; from daily wellness tracking to precision clinical health metrics with",
        bandTitleRegular: "Smart Bands",
        bandTitleHighlighted: "Delband",
        bandDescHighlight: "Two Smart & Versatile Choices",
        bandDescText: "Tailored to your needs; from daily health monitoring to precision vital tracking with",
        suitableForLabel: "Suitable for:",
        alloyLabel: "Material:",
        weightLabel: "Weight:",
        batteryLabel: "Battery life:",
        sensorsLabel: "Sensors:",
        resistanceLabel: "Resistance:",
        andLabel: "and",
    },
    ar: {
        ringTitleRegular: "الخواتم الذكية",
        ringTitleHighlighted: "دلبند",
        ringDescHighlight: "خياران ذكيان ومتقدمان",
        ringDescText: "لتلبية احتياجاتك المختلفة؛ من المتابعة الصحية اليومية إلى القياسات الدقيقة للبيانات الصحية مع",
        bandTitleRegular: "الأساور الذكية",
        bandTitleHighlighted: "دلبند",
        bandDescHighlight: "خياران ذكيان وعمليان",
        bandDescText: "لتلبية احتياجاتك المختلفة؛ من المتابعة الصحية اليومية إلى الرصد الدقيق للعلامات الحيوية باحترافية مع",
        suitableForLabel: "مناسب لـ:",
        alloyLabel: "مادة الهيكل:",
        weightLabel: "الوزن:",
        batteryLabel: "عمر البطارية:",
        sensorsLabel: "المستشعرات:",
        resistanceLabel: "مقاومة الماء:",
        andLabel: "و",
    },
    tr: {
        ringTitleRegular: "Akıllı Yüzükler",
        ringTitleHighlighted: "Delband",
        ringDescHighlight: "İki Akıllı ve Gelişmiş Seçenek",
        ringDescText: "Farklı ihtiyaçlarınız için; günlük sağlık takibinden hassas klinik sağlık analizine kadar",
        bandTitleRegular: "Akıllı Bileklikler",
        bandTitleHighlighted: "Delband",
        bandDescHighlight: "İki Akıllı ve Kullanışlı Seçenek",
        bandDescText: "Farklı ihtiyaçlarınız için; günlük sağlık takibinden hassas yaşamsal veri takibine kadar",
        suitableForLabel: "Şunun için uygun:",
        alloyLabel: "Gövde malzemesi:",
        weightLabel: "Ağırlık:",
        batteryLabel: "Pil ömrü:",
        sensorsLabel: "Sensörler:",
        resistanceLabel: "Dayanıklılık:",
        andLabel: "ve",
    },
};

// Detailed product profiles for key comparison models across languages
const PRODUCT_PROFILES: Record<string, Record<string, {
    slogan: string;
    suitableFor: string;
    alloy: string;
    weight: string;
    batteryLife: string;
    sensors: string[];
    resistance: string;
}>> = {
    "EVR-X3": {
        fa: {
            slogan: "سنجش دقیق اکسیژن و پایش پیشرفته کیفیت خواب",
            suitableFor: "پایش دقیق سطح اکسیژن و کیفیت تنفس در خواب",
            alloy: "آلیاژ تیتانیوم درجه هوافضا",
            weight: "۳.۴ گرم (بسیار باریک ۲.۷mm)",
            batteryLife: "۷+ روز (شارژ بی‌سیم سریع)",
            sensors: ["سنسور دقیق SpO2", "پایش الگوی تنفس", "روند انرژی و متابولیسم", "HRV", "دمای پوست"],
            resistance: "5ATM (ضد آب تا ۵۰ متر)",
        },
        en: {
            slogan: "Precision SpO2 & Advanced Sleep Tracking",
            suitableFor: "Precise blood oxygen & sleep breathing monitoring",
            alloy: "Aerospace-Grade Titanium",
            weight: "3.4g (Ultra-slim 2.7mm)",
            batteryLife: "7+ Days (Fast Wireless Charge)",
            sensors: ["Precision SpO2", "Breathing Pattern", "Energy & Metabolism", "HRV", "Skin Temp"],
            resistance: "5ATM (Waterproof 50m)",
        },
        ar: {
            slogan: "قياس دقيق للأكسجين وتتبع سريري للنوم",
            suitableFor: "متابعة دقيقة للأكسجين والتنفس أثناء النوم",
            alloy: "سبائك تيتانيوم من الدرجة الفضائية",
            weight: "3.4 جم (نحيف للغاية 2.7 مم)",
            batteryLife: "+7 أيام (شحن لاسلكي سريع)",
            sensors: ["مستشعر SpO2 طبي", "انقطاع التنفس AHI", "سكر الدم BGEM", "HRV", "حرارة الجلد"],
            resistance: "5ATM (مقاوم للماء 50م)",
        },
        tr: {
            slogan: "Hassas SpO2 ve Klinik Uyku Takibi",
            suitableFor: "Hassas kan oksijeni ve uyku apnesi takibi",
            alloy: "Havacılık Sınıfı Titanyum",
            weight: "3.4g (Ultra ince 2.7mm)",
            batteryLife: "7+ Gün (Hızlı Kablosuz Şarj)",
            sensors: ["Medikal SpO2", "AHI Uyku Apnesi", "BGEM Şeker", "HRV", "Cilt Sıcaklığı"],
            resistance: "5ATM (50m Su Geçirmez)",
        },
    },
    "EVR-X5": {
        fa: {
            slogan: "کنترل لمسی هوشمند با بدنه سرامیک/تیتانیوم",
            suitableFor: "کنترل لمسی دستگاه‌ها و پایش ۵۴+ ورزش",
            alloy: "سرامیک پیشرفته زیرکونیا / تیتانیوم",
            weight: "فوق‌العاده باریک ۲.۲mm",
            batteryLife: "۵ تا ۱۰ روز",
            sensors: ["کنترل لمسی Touch", "۵۴+ مود ورزشی", "SpO2 پیوسته", "پایش خواب", "HRV"],
            resistance: "5ATM (مناسب شنا)",
        },
        en: {
            slogan: "Smart Touch Control with Ceramic/Titanium Body",
            suitableFor: "Smart touch gestures & 54+ sports tracking",
            alloy: "Advanced Zirconia Ceramic / Titanium",
            weight: "Ultra-slim 2.2mm profile",
            batteryLife: "5 to 10 Days",
            sensors: ["Touch Control", "54+ Sport Modes", "Continuous SpO2", "Sleep Track", "HRV"],
            resistance: "5ATM (Swimming Ready)",
        },
        ar: {
            slogan: "تحكم باللمس الذكي مع هيكل سيراميك/تيتانيوم",
            suitableFor: "إيماءات اللمس الذكية وتتبع 54+ رياضة",
            alloy: "سيراميك الزركونيا المتطور / تيتانيوم",
            weight: "نحيف للغاية بسمك 2.2 مم",
            batteryLife: "5 إلى 10 أيام",
            sensors: ["تحكم باللمس", "54+ وضع رياضي", "SpO2 مستمر", "تتبع النوم", "HRV"],
            resistance: "5ATM (مناسب للسباحة)",
        },
        tr: {
            slogan: "Seramik/Titanyum Gövdeli Akıllı Dokunmatik Kontrol",
            suitableFor: "Akıllı dokunmatik hareketler ve 54+ spor takibi",
            alloy: "Gelişmiş Zirkonya Seramik / Titanyum",
            weight: "Ultra ince 2.2mm profil",
            batteryLife: "5 - 10 Gün",
            sensors: ["Dokunmatik Kontrol", "54+ Spor Modu", "Sürekli SpO2", "Uyku Takibi", "HRV"],
            resistance: "5ATM (Yüzmeye Uygun)",
        },
    },
    "EVB-E50": {
        fa: {
            slogan: "سنسور ECG الکترودی و پایش جامع روند انرژی و گردش خون",
            suitableFor: "ثبت نوار قلب، پایش گردش خون و ارزیابی روند انرژی",
            alloy: "آلیاژ روی باکیفیت + بدنه پایینی PC",
            weight: "۴۲ گرم (نمایشگر 1.47 اینچ)",
            batteryLife: "۷ تا ۱۴ روز (آماده‌باش تا ۳۰ روز)",
            sensors: ["نوار قلب ECG", "پایش گردش خون", "روند انرژی و متابولیسم", "دمای پوست", "HRV", "ضربان ۲۴/۷"],
            resistance: "IP68 (مقاوم در برابر آب و گردوغبار)",
        },
        en: {
            slogan: "Electrode ECG & Comprehensive Energy Trend & Circulation Tracking",
            suitableFor: "ECG recording, circulation & energy trend tracking",
            alloy: "Premium Zinc Alloy + PC Shell",
            weight: "42g (1.47\" Display)",
            batteryLife: "7 to 14 Days (Up to 30 Days Standby)",
            sensors: ["ECG Monitor", "Circulation Tracking", "Energy & Metabolism Trend", "Skin Temp", "HRV", "24/7 Heart Rate"],
            resistance: "IP68 (Water & Dust Resistant)",
        },
        ar: {
            slogan: "مستشعر ECG الكهربائي ومراقبة شاملة لمسار الطاقة والدورة الدموية",
            suitableFor: "تخطيط القلب ECG، مراقبة الدورة الدموية ومسار الطاقة",
            alloy: "سبائك الزنك الفاخرة + هيكل PC",
            weight: "42 جم (شاشة 1.47 بوصة)",
            batteryLife: "7 إلى 14 يوماً (استعداد حتى 30 يوماً)",
            sensors: ["تخطيط ECG", "الدورة الدموية", "مسار الطاقة", "حرارة الجلد", "HRV", "نبض 24/7"],
            resistance: "IP68 (مقاوم للماء والغبار)",
        },
        tr: {
            slogan: "Elektrotlu EKG & Kapsamlı Enerji ve Dolaşım Takibi",
            suitableFor: "EKG kaydı, dolaşım ve enerji eğilimi analizi",
            alloy: "Premium Çinko Alaşım + PC Gövde",
            weight: "42g (1.47\" Ekran)",
            batteryLife: "7 - 14 Gün (30 güne kadar bekleme)",
            sensors: ["EKG Takibi", "Dolaşım Takibi", "Enerji ve Metabolizma", "Cilt Sıcaklığı", "HRV", "7/24 Nabız"],
            resistance: "IP68 (Suya ve Toza Dayanıklı)",
        },
    },
    "EVB-V10": {
        fa: {
            slogan: "مربی هوش مصنوعی، روند انرژی و استیل ضدزنگ",
            suitableFor: "تحلیل هوشمند ریکاوری، ارزیابی روند انرژی و سبک زندگی ورزشی",
            alloy: "استیل ضدزنگ ۳۱۶L با استاندارد ۳ATM",
            weight: "۳۲ گرم (طراحی ارگونومیک)",
            batteryLife: "۷ تا ۱۰ روز",
            sensors: ["مربی هوش مصنوعی", "روند انرژی و متابولیسم", "پایش گردش خون", "دمای پوست", "HRV", "الگوی تنفس خواب"],
            resistance: "3ATM (مقاوم در برابر نفوذ آب)",
        },
        en: {
            slogan: "AI Health Coach, Energy Trend & Stainless Steel",
            suitableFor: "AI health guidance, energy tracking & active fitness",
            alloy: "316L Stainless Steel (3ATM)",
            weight: "32g (Ergonomic Design)",
            batteryLife: "7 to 10 Days",
            sensors: ["AI Health Coach", "Energy Trend", "Circulation Tracking", "Skin Temp", "HRV", "Sleep Breathing Pattern"],
            resistance: "3ATM (Water-Resistant)",
        },
        ar: {
            slogan: "مدرب الصحة بالذكاء الاصطناعي ومسار الطاقة والفولاذ",
            suitableFor: "تحليل الذكاء الاصطناعي، تتبع الطاقة واللياقة النشطة",
            alloy: "فولاذ مقاوم للصدأ 316L (معيار 3ATM)",
            weight: "32 جم (تصميم مريح)",
            batteryLife: "7 إلى 10 أيام",
            sensors: ["مدرب AI الذكي", "مسار الطاقة", "الدورة الدموية", "حرارة الجلد", "HRV", "نمط تنفس النوم"],
            resistance: "3ATM (مقاوم للماء)",
        },
        tr: {
            slogan: "AI Sağlık Koçu, Enerji Eğilimi ve Paslanmaz Çelik",
            suitableFor: "AI sağlık koçluğu, enerji takibi ve aktif spor yaşamı",
            alloy: "316L Paslanmaz Çelik (3ATM)",
            weight: "32g (Ergonomik Tasarım)",
            batteryLife: "7 - 10 Gün",
            sensors: ["AI Sağlık Koçu", "Enerji Eğilimi", "Dolaşım Takibi", "Cilt Sıcaklığı", "HRV", "Uyku Solunum Düzeni"],
            resistance: "3ATM (Suya Dayanıklı)",
        },
    },
};

const getLocalizedRingData = (id: string, lang: string): RingData => {
    const langKey = ["fa", "en", "ar", "tr"].includes(lang) ? lang : "fa";

    // Lookup base product in smartRingsData or smartBandsData
    const product =
        smartRingsData.allProducts.find((p) => p.id.toLowerCase() === id.toLowerCase()) ||
        smartBandsData.allProducts.find((p) => p.id.toLowerCase() === id.toLowerCase());

    const title = product ? product.title : id.toUpperCase();
    const icon = product
        ? product.icon
        : id.startsWith("EVB")
        ? "/media/hero-band/band3.webp"
        : "/media/products/rings/EVR-X3.webp";

    const gradientParts = product?.colorClass ? product.colorClass.split(" ") : ["from-blue-600", "to-blue-800"];
    const gradientFrom = gradientParts.find((p) => p.startsWith("from-")) || "from-blue-600";
    const gradientTo = gradientParts.find((p) => p.startsWith("to-")) || "to-blue-800";

    // Check specific profiled models
    const matchedProfileKey = Object.keys(PRODUCT_PROFILES).find(
        (key) => key.toLowerCase() === id.toLowerCase()
    );

    if (matchedProfileKey && PRODUCT_PROFILES[matchedProfileKey][langKey]) {
        const p = PRODUCT_PROFILES[matchedProfileKey][langKey];
        return {
            id: matchedProfileKey,
            name: matchedProfileKey,
            slogan: p.slogan,
            suitableFor: p.suitableFor,
            alloy: p.alloy,
            weight: p.weight,
            batteryLife: p.batteryLife,
            sensors: p.sensors.map((s) => ({ name: s })),
            resistance: p.resistance,
            colors: [{ color: `bg-gradient-to-r ${product?.colorClass || "from-blue-500 to-indigo-600"}`, bg: "border-gray-400", border: "border-gray-400" }],
            imagePath: icon,
            backgroundPath:
                matchedProfileKey === "EVR-X5" || matchedProfileKey === "EVB-V10"
                    ? "/media/ComparisonSlider/Right-Background.avif"
                    : "/media/ComparisonSlider/Left-Background.avif",
            gradientFrom,
            gradientTo,
            hoverBgColor: `hover:bg-${gradientFrom.split("-")[1] || "blue"}-50/30`,
        };
    }

    // Generic fallback for any other product ID
    const getFeatureVal = (name: string) => {
        if (!product) return "-";
        const f = product.features.find((f) => f.feature === name);
        return f ? String(f.value) : "-";
    };

    const sensors: RingSensor[] = [];
    if (product) {
        product.features.forEach((f) => {
            if (f.value === true) {
                sensors.push({ name: f.feature });
            }
        });
    }

    return {
        id: product ? product.id : id,
        name: title,
        slogan: langKey === "en" ? "Smart & Precision Health" : langKey === "ar" ? "صحة ذكية ودقيقة" : langKey === "tr" ? "Akıllı ve Hassas Sağlık" : "هوشمند و دقیق",
        suitableFor: langKey === "en" ? "Daily health and wellness tracking" : langKey === "ar" ? "متابعة الصحة اليومية والنشاط" : langKey === "tr" ? "Günlük sağlık ve aktivite takibi" : "استفاده روزمره و پایش سلامت",
        alloy: getFeatureVal("جنس بدنه") !== "-" ? getFeatureVal("جنس بدنه") : (langKey === "en" ? "Aerospace Alloy" : "آلیاژ مقاوم"),
        weight: getFeatureVal("وزن") !== "-" ? getFeatureVal("وزن") : (id.startsWith("EVB") ? "32g" : "3.5g"),
        batteryLife: getFeatureVal("عمر باتری") !== "-" ? getFeatureVal("عمر باتری") : "7 Days",
        sensors: sensors.length > 0 ? sensors.slice(0, 5) : [{ name: "24/7 Health Sensors" }],
        resistance: getFeatureVal("گواهی ضد آب") !== "-" ? getFeatureVal("گواهی ضد آب") : "5ATM",
        colors: [{ color: `bg-gradient-to-r ${product?.colorClass || "from-blue-500 to-indigo-600"}`, bg: "border-gray-400", border: "border-gray-400" }],
        imagePath: icon,
        backgroundPath: "/media/ComparisonSlider/Left-Background.avif",
        gradientFrom,
        gradientTo,
        hoverBgColor: `hover:bg-blue-50/30`,
    };
};

type SliderProps = {
    initialPosition?: number;
    leftId?: string;
    rightId?: string;
    leftRingId?: string;
    rightRingId?: string;
    productType?: "ring" | "band";
    titleRegular?: string;
    titleHighlighted?: string;
    descriptionHighlight?: string;
    descriptionText?: string;
};

const ComparisonSlider: React.FC<SliderProps> = ({
    initialPosition = 50,
    leftId,
    rightId,
    leftRingId,
    rightRingId,
    productType = "ring",
    titleRegular,
    titleHighlighted,
    descriptionHighlight,
    descriptionText,
}) => {
    const { i18n } = useTranslation();
    const currentLang = (i18n.language || "fa").split("-")[0];
    const langKey = ["fa", "en", "ar", "tr"].includes(currentLang) ? currentLang : "fa";
    const isRtl = langKey === "fa" || langKey === "ar";
    const t = SLIDER_I18N[langKey] || SLIDER_I18N.fa;

    // State & Refs
    const [sliderPosition, setSliderPosition] = useState(initialPosition);
    const containerRef = useRef<HTMLDivElement>(null);
    const sliderRef = useRef<HTMLInputElement>(null);
    const isDragging = useRef(false);

    // Responsive hooks
    const isMobile = useMediaQuery("(max-width: 768px)");
    const isTablet = useMediaQuery("(max-width: 1024px)");

    // Default IDs for Ring vs Band
    const defaultLeftId = productType === "band" ? "EVB-E50" : "EVR-X3";
    const defaultRightId = productType === "band" ? "EVB-V10" : "EVR-X5";

    const finalLeftId = leftId || leftRingId || defaultLeftId;
    const finalRightId = rightId || rightRingId || defaultRightId;

    // Localized product data
    const leftRing = getLocalizedRingData(finalLeftId, langKey);
    const rightRing = getLocalizedRingData(finalRightId, langKey);

    const updateSliderPosition = (value: number): void => {
        const newPosition = Math.max(0, Math.min(100, value));
        setSliderPosition(newPosition);
        updateDOMElements(newPosition);
    };

    const updateDOMElements = (position: number): void => {
        if (sliderRef.current) {
            sliderRef.current.value = String(position);
        }

        const elements = {
            sliderControl: containerRef.current?.querySelector(".slider-control") as HTMLElement | null,
            leftImage: containerRef.current?.querySelector(".left-image-container") as HTMLElement | null,
            rightImage: containerRef.current?.querySelector(".right-image-container") as HTMLElement | null,
        };

        if (elements.sliderControl) {
            elements.sliderControl.style.left = `${position}%`;
        }

        if (elements.leftImage) {
            elements.leftImage.style.clipPath = `polygon(0 0, ${position}% 0, ${position}% 100%, 0 100%)`;
        }

        if (elements.rightImage) {
            elements.rightImage.style.clipPath = `polygon(${position}% 0, 100% 0, 100% 100%, ${position}% 100%)`;
        }
    };

    const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        updateSliderPosition(Number(e.target.value));
    };

    const handleSliderInput = (e: React.FormEvent<HTMLInputElement>): void => {
        updateSliderPosition(Number((e.target as HTMLInputElement).value));
        isDragging.current = true;
    };

    const handleSliderEnd = (): void => {
        isDragging.current = false;
    };

    const handleContainerClick = (e: React.MouseEvent): void => {
        if (!containerRef.current) return;
        const containerRect = containerRef.current.getBoundingClientRect();
        const offsetX = e.clientX - containerRect.left;
        const newPosition = (offsetX / containerRect.width) * 100;
        updateSliderPosition(newPosition);
    };

    const styles = {
        container: {
            position: "relative",
            height: isMobile ? "500px" : isTablet ? "600px" : "700px",
            overflow: "hidden",
            userSelect: "none",
            touchAction: "none",
        } as CSSProperties,

        slider: {
            position: "absolute",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            opacity: "0",
            cursor: "ew-resize",
            zIndex: "30",
            margin: "0",
            appearance: "none",
            WebkitAppearance: "none",
        } as CSSProperties,

        sliderControl: {
            position: "absolute",
            top: "0",
            bottom: "0",
            left: `${sliderPosition}%`,
            width: "1px",
            zIndex: "20",
            transform: "translateX(-50%)",
            pointerEvents: "none",
        } as CSSProperties,

        leftImage: {
            position: "absolute",
            top: "0",
            left: "0",
            height: "100%",
            width: "100%",
            clipPath: `polygon(0 0, ${sliderPosition}% 0, ${sliderPosition}% 100%, 0 100%)`,
            transition: "none",
            willChange: "clip-path",
        } as CSSProperties,

        rightImage: {
            position: "absolute",
            top: "0",
            right: "0",
            height: "100%",
            width: "100%",
            clipPath: `polygon(${sliderPosition}% 0, 100% 0, 100% 100%, ${sliderPosition}% 100%)`,
            transition: "none",
            willChange: "clip-path",
        } as CSSProperties,
    };

    // Product Specification Card Inside Slider
    const ProductSpecCard = ({ ring, position }: { ring: RingData; position: "left" | "right" }) => {
        const isLeft = position === "left";
        const [activeFeature, setActiveFeature] = useState<string | null>(null);

        useEffect(() => {
            if (isMobile) {
                const features = ["suitableFor", "alloy", "weight", "batteryLife", "sensors", "resistance"];
                let index = 0;
                const interval = setInterval(() => {
                    setActiveFeature(features[index]);
                    index = (index + 1) % features.length;
                }, 3000);
                return () => clearInterval(interval);
            }
        }, [isMobile]);

        const handleFeatureClick = (feature: string) => {
            setActiveFeature(activeFeature === feature ? null : feature);
        };

        const accentGlow = isLeft ? "rgba(37,99,235,0.18)" : "rgba(245,158,11,0.18)";
        const accentBorder = isLeft ? "border-blue-200/80 hover:border-blue-400" : "border-orange-200/80 hover:border-orange-400";
        const badgeBg = isLeft ? "bg-blue-100/80 text-blue-800 border-blue-200" : "bg-amber-100/80 text-amber-900 border-amber-200";

        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: isLeft ? 0.2 : 0.4 }}
                className={`z-10 absolute text-center ${isLeft ? "left-[3%] sm:left-[4%]" : "right-[3%] sm:right-[4%]"} ${
                    isMobile ? "top-[3%]" : "top-[6%] sm:top-[8%]"
                } text-slate-800 max-w-[280px] sm:max-w-[340px] pointer-events-auto`}
            >
                {/* Title & Slogan */}
                <div className="relative inline-flex flex-col items-center">
                    <motion.div
                        whileHover={{ scale: 1.04 }}
                        transition={{ type: "spring", stiffness: 400, damping: 12 }}
                        className="relative group cursor-pointer"
                    >
                        <h2 className={`${isMobile ? "text-2xl sm:text-3xl" : "text-3xl sm:text-4xl md:text-5xl"} font-black bg-clip-text bg-gradient-to-r ${ring.gradientFrom} ${ring.gradientTo} text-transparent tracking-tight relative drop-shadow-2xs`}>
                            {ring.name}
                        </h2>
                        <motion.div
                            initial={{ width: "0%" }}
                            animate={{ width: "100%" }}
                            transition={{ duration: 1.2, delay: 0.4, ease: "easeInOut" }}
                            className={`h-0.5 rounded-full mt-0.5 bg-gradient-to-r ${ring.gradientFrom} ${ring.gradientTo}`}
                        />
                    </motion.div>
                </div>

                {/* Slogan Pill */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                    className="mt-1.5"
                >
                    <span className={`px-3 py-1 rounded-full inline-block backdrop-blur-md bg-white/80 border border-white/90 shadow-2xs ${isMobile ? "text-[11px] sm:text-xs" : "text-xs sm:text-sm"} font-bold text-slate-800`}>
                        {ring.slogan}
                    </span>
                </motion.div>

                {/* Interactive Specs Card */}
                <motion.div
                    whileHover={{ y: -2 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    className={`mt-2.5 bg-white/85 backdrop-blur-xl ${
                        isMobile ? "p-2.5 sm:p-3" : "p-4"
                    } rounded-2xl sm:rounded-3xl shadow-xl border border-white/90 transition-all duration-300 relative overflow-hidden text-right`}
                    style={{
                        boxShadow: `0 14px 36px -8px ${accentGlow}, 0 2px 8px rgba(0,0,0,0.04)`,
                    }}
                >
                    {/* Top gradient accent line */}
                    <div className={`absolute top-0 inset-x-0 h-1 bg-gradient-to-r ${ring.gradientFrom} ${ring.gradientTo}`} />

                    <ul className={`${isRtl ? "text-right" : "text-left"} ${isMobile ? "text-[11px]" : "text-xs sm:text-sm"} space-y-1.5 sm:space-y-2`}>
                        {/* Suitable For */}
                        <li
                            onClick={() => handleFeatureClick("suitableFor")}
                            className={`flex items-center justify-between gap-2 p-1.5 sm:p-2 rounded-xl bg-slate-50/70 hover:bg-white border ${accentBorder} transition-all duration-200 cursor-pointer shadow-2xs`}
                        >
                            <div className="flex items-center gap-1.5 shrink-0 min-w-0">
                                <div className={`w-5 h-5 rounded-lg flex items-center justify-center shrink-0 ${isLeft ? "bg-blue-100 text-blue-600" : "bg-orange-100 text-orange-600"}`}>
                                    <Target className="w-3 h-3" />
                                </div>
                                <span className="font-bold text-slate-900 shrink-0">{t.suitableForLabel}</span>
                            </div>
                            <span className="text-slate-700 font-medium truncate text-left max-w-[150px] sm:max-w-[180px]">
                                {ring.suitableFor}
                            </span>
                        </li>

                        {/* Alloy / Material */}
                        <li
                            onClick={() => handleFeatureClick("alloy")}
                            className={`flex items-center justify-between gap-2 p-1.5 sm:p-2 rounded-xl bg-slate-50/70 hover:bg-white border ${accentBorder} transition-all duration-200 cursor-pointer shadow-2xs`}
                        >
                            <div className="flex items-center gap-1.5 shrink-0 min-w-0">
                                <div className={`w-5 h-5 rounded-lg flex items-center justify-center shrink-0 ${isLeft ? "bg-blue-100 text-blue-600" : "bg-orange-100 text-orange-600"}`}>
                                    <Layers className="w-3 h-3" />
                                </div>
                                <span className="font-bold text-slate-900 shrink-0">{t.alloyLabel}</span>
                            </div>
                            <span className="text-slate-700 font-medium truncate text-left max-w-[150px] sm:max-w-[180px]">
                                {ring.alloy}
                            </span>
                        </li>

                        {/* Weight & Battery (Compact 2-col) */}
                        <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                            {/* Weight */}
                            <li
                                onClick={() => handleFeatureClick("weight")}
                                className={`flex flex-col gap-1 p-1.5 sm:p-2 rounded-xl bg-slate-50/70 hover:bg-white border ${accentBorder} transition-all duration-200 cursor-pointer shadow-2xs`}
                            >
                                <div className="flex items-center gap-1 text-slate-900 font-bold text-[10px] sm:text-xs">
                                    <Scale className={`w-3 h-3 ${isLeft ? "text-blue-500" : "text-orange-500"}`} />
                                    <span>{t.weightLabel}</span>
                                </div>
                                <span className={`px-2 py-0.5 rounded-lg text-[10px] sm:text-[11px] font-bold self-start border ${badgeBg}`}>
                                    {ring.weight}
                                </span>
                            </li>

                            {/* Battery Life */}
                            <li
                                onClick={() => handleFeatureClick("batteryLife")}
                                className={`flex flex-col gap-1 p-1.5 sm:p-2 rounded-xl bg-slate-50/70 hover:bg-white border ${accentBorder} transition-all duration-200 cursor-pointer shadow-2xs`}
                            >
                                <div className="flex items-center gap-1 text-slate-900 font-bold text-[10px] sm:text-xs">
                                    <Zap className={`w-3 h-3 ${isLeft ? "text-blue-500" : "text-orange-500"}`} />
                                    <span>{t.batteryLabel}</span>
                                </div>
                                <span className="text-slate-800 font-bold text-[10px] sm:text-[11px] truncate">
                                    {ring.batteryLife}
                                </span>
                            </li>
                        </div>

                        {/* Sensors */}
                        <li
                            onClick={() => handleFeatureClick("sensors")}
                            className={`flex flex-col gap-1.5 p-2 rounded-xl bg-slate-50/70 hover:bg-white border ${accentBorder} transition-all duration-200 cursor-pointer shadow-2xs`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5">
                                    <div className={`w-4 h-4 rounded-md flex items-center justify-center ${isLeft ? "bg-blue-100 text-blue-600" : "bg-orange-100 text-orange-600"}`}>
                                        <Activity className="w-2.5 h-2.5" />
                                    </div>
                                    <span className="font-bold text-slate-900">{t.sensorsLabel}</span>
                                </div>
                                <span className="text-[10px] text-slate-400 font-medium">({ring.sensors.length})</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {ring.sensors.map((sensor, idx) => (
                                    <span
                                        key={idx}
                                        className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${
                                            isLeft
                                                ? "bg-blue-50 text-blue-700 border border-blue-200/80"
                                                : "bg-amber-50 text-amber-800 border border-amber-200/80"
                                        }`}
                                    >
                                        {sensor.name}
                                    </span>
                                ))}
                            </div>
                        </li>

                        {/* Resistance */}
                        <li
                            onClick={() => handleFeatureClick("resistance")}
                            className={`flex items-center justify-between gap-2 p-1.5 sm:p-2 rounded-xl bg-slate-50/70 hover:bg-white border ${accentBorder} transition-all duration-200 cursor-pointer shadow-2xs`}
                        >
                            <div className="flex items-center gap-1.5 shrink-0 min-w-0">
                                <div className={`w-5 h-5 rounded-lg flex items-center justify-center shrink-0 ${isLeft ? "bg-blue-100 text-blue-600" : "bg-orange-100 text-orange-600"}`}>
                                    <Waves className="w-3 h-3" />
                                </div>
                                <span className="font-bold text-slate-900 shrink-0">{t.resistanceLabel}</span>
                            </div>
                            <span className="text-slate-800 font-bold truncate text-left">
                                {ring.resistance}
                            </span>
                        </li>
                    </ul>
                </motion.div>
            </motion.div>
        );
    };

    const regularText = titleRegular || (productType === "band" ? t.bandTitleRegular : t.ringTitleRegular);
    const highlightedText = titleHighlighted || (productType === "band" ? t.bandTitleHighlighted : t.ringTitleHighlighted);
    const descHighlight = descriptionHighlight || (productType === "band" ? t.bandDescHighlight : t.ringDescHighlight);
    const descText = descriptionText || (productType === "band" ? t.bandDescText : t.ringDescText);

    return (
        <div dir={isRtl ? "rtl" : "ltr"} className="pt-16 sm:pt-24 md:pt-28 px-3 sm:px-8 flex flex-col items-center justify-center">
            <SectionHeader
                regularText={regularText}
                highlightedText={highlightedText}
                descriptionHighlight={descHighlight}
                descriptionText={descText}
                descriptionExtra={
                    <>
                        <span className="text-blue-600 font-bold mx-1">{leftRing.name}</span>
                        {t.andLabel}
                        <span className="text-orange-500 font-bold mx-1">{rightRing.name}</span>
                    </>
                }
                className="mb-6"
            />

            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-7xl w-full mx-auto rounded-2xl shadow-xl overflow-hidden border border-slate-200/90"
            >
                <div ref={containerRef} style={styles.container} onClick={handleContainerClick}>
                    {/* Native Drag Range Slider */}
                    <input
                        ref={sliderRef}
                        type="range"
                        min="0"
                        max="100"
                        value={sliderPosition}
                        onChange={handleSliderChange}
                        onInput={handleSliderInput}
                        onMouseUp={handleSliderEnd}
                        onTouchEnd={handleSliderEnd}
                        dir="ltr"
                        style={styles.slider}
                        aria-label="Comparison slider"
                        data-image-comparison-range
                    />

                    {/* Left image container */}
                    <div className="left-image-container" style={styles.leftImage}>
                        <div className="h-full w-full relative">
                            <motion.img
                                initial={{ scale: 1.05, opacity: 0.8 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 1 }}
                                src={leftRing.backgroundPath}
                                alt={`${leftRing.name} background`}
                                className="absolute top-0 left-0 h-full w-full object-cover z-0"
                            />
                            <div className="h-full w-full flex items-center justify-between relative">
                                <ProductSpecCard ring={leftRing} position="left" />
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <motion.img
                                        initial={{ y: 20, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ duration: 0.8, delay: 0.3 }}
                                        whileHover={{ scale: 1.05, rotate: 4 }}
                                        src={leftRing.imagePath}
                                        alt={leftRing.name}
                                        className={`object-contain drop-shadow-2xl ${
                                            isMobile ? "h-[220px] w-[220px]" : isTablet ? "h-[290px] w-[290px]" : "h-[390px] w-[390px]"
                                        }`}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right image container */}
                    <div className="right-image-container" style={styles.rightImage}>
                        <div className="h-full w-full relative">
                            <motion.img
                                initial={{ scale: 1.05, opacity: 0.8 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 1 }}
                                src={rightRing.backgroundPath}
                                alt={`${rightRing.name} background`}
                                className="absolute top-0 left-0 h-full w-full object-cover z-0"
                            />
                            <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                                <motion.img
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ duration: 0.8, delay: 0.3 }}
                                    whileHover={{ scale: 1.05, rotate: -4 }}
                                    src={rightRing.imagePath}
                                    alt={rightRing.name}
                                    className={`object-contain drop-shadow-2xl ${
                                        isMobile ? "h-[220px] w-[220px]" : isTablet ? "h-[290px] w-[290px]" : "h-[390px] w-[390px]"
                                    }`}
                                />
                            </div>
                            <div className="relative z-20 flex items-center justify-end h-full">
                                <ProductSpecCard ring={rightRing} position="right" />
                            </div>
                        </div>
                    </div>

                    {/* Handle Controller */}
                    <div className="slider-control" style={styles.sliderControl} data-image-comparison-slider>
                        {/* Divider Line */}
                        <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-0.5 bg-gradient-to-b from-transparent via-white/90 to-transparent shadow-xs" />

                        {/* Interactive Handle Knob */}
                        <motion.div
                            whileHover={{ scale: 1.12 }}
                            whileTap={{ scale: 0.92 }}
                            transition={{ type: "spring", stiffness: 400, damping: 15 }}
                            className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${
                                isMobile ? "w-10 h-10" : "w-13 h-13"
                            } bg-white/95 backdrop-blur-md rounded-full flex items-center justify-center shadow-xl border border-slate-200/90 hover:border-blue-400 cursor-ew-resize group text-slate-700 hover:text-blue-600 transition-colors`}
                        >
                            <div className="flex items-center justify-center gap-1" dir="ltr">
                                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 stroke-[2.5] transition-transform group-hover:-translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                </svg>
                                <div className="w-0.5 h-4 bg-slate-300 group-hover:bg-blue-400 transition-colors rounded-full" />
                                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 stroke-[2.5] transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default ComparisonSlider;
